import { useQuery as useReferralBonusOffers } from "@faire/web-api/api/brand-referrals/retailer/retailerToken/referral-bonus-offers/get-hooks";
import { getGlobalProperty } from "@faire/web/common/globals/getGlobalProperty";
import isUndefined from "lodash/isUndefined";

import { useAppNudgeStore } from "@faire/retailer/features/Nudges/useAppNudgeStore";
import { getSettingEnableReferralAdoption } from "@faire/retailer/settings/getSettingEnableReferralAdoption";
import { useRetailerStore } from "@faire/retailer/stores/domain/RetailerStore";
import {
  useMilestone,
  useIsMilestoneOverWeekOld,
} from "@faire/retailer/stores/domain/useMilestone";

interface IReengageReferralNudgeEligibility {
  shouldShowReengageReferralNudge: boolean;
  nudgeToShow: "single" | "multi";
  currentMilestone:
    | false
    | "FIRST_REFERRAL_REENGAGEMENT_RETAILER_NUDGE_SEEN"
    | "SECOND_REFERRAL_REENGAGEMENT_RETAILER_NUDGE_SEEN"
    | "THIRD_REFERRAL_REENGAGEMENT_RETAILER_NUDGE_SEEN";
  hasAcknowledgedReengageReferralNudge: boolean;
  setAppNudgeStore: ReturnType<typeof useAppNudgeStore>[1];
}

export const useReengageReferralNudgeEligibility: () => IReengageReferralNudgeEligibility =
  () => {
    const [{ hasAcknowledgedReengageReferralNudge }, setAppNudgeStore] =
      useAppNudgeStore(["hasAcknowledgedReengageReferralNudge"]);
    const { retailerToken } = useRetailerStore(["retailerToken"]);
    const { data: referralOffersData } = useReferralBonusOffers(
      retailerToken ?? "",
      { enabled: !!retailerToken }
    );

    const { hasOccurred: hasSeenNudge1 } = useMilestone(
      "FIRST_REFERRAL_REENGAGEMENT_RETAILER_NUDGE_SEEN"
    );
    const { hasOccurred: hasSeenNudge2 } = useMilestone(
      "SECOND_REFERRAL_REENGAGEMENT_RETAILER_NUDGE_SEEN"
    );
    const { hasOccurred: hasSeenNudge3 } = useMilestone(
      "THIRD_REFERRAL_REENGAGEMENT_RETAILER_NUDGE_SEEN"
    );

    const firstNudgeWeekStale = useIsMilestoneOverWeekOld(
      "FIRST_REFERRAL_REENGAGEMENT_RETAILER_NUDGE_SEEN"
    );
    const secondNudgeWeekStale = useIsMilestoneOverWeekOld(
      "SECOND_REFERRAL_REENGAGEMENT_RETAILER_NUDGE_SEEN"
    );

    const currentMilestone =
      (!hasSeenNudge1 && "FIRST_REFERRAL_REENGAGEMENT_RETAILER_NUDGE_SEEN") ||
      (!hasSeenNudge2 &&
        firstNudgeWeekStale &&
        "SECOND_REFERRAL_REENGAGEMENT_RETAILER_NUDGE_SEEN") ||
      (!hasSeenNudge3 &&
        secondNudgeWeekStale &&
        "THIRD_REFERRAL_REENGAGEMENT_RETAILER_NUDGE_SEEN");

    const brandsToRefer =
      getGlobalProperty<Array<string>>("referralBonusOfferReferralNames") || [];

    const nudgeToShow = brandsToRefer.length > 1 ? "multi" : "single";

    const shouldShowReengageReferralNudge =
      brandsToRefer?.length !== 0 &&
      hasAcknowledgedReengageReferralNudge === false &&
      currentMilestone &&
      isUndefined(referralOffersData) === false &&
      referralOffersData!.offers?.length > 0 &&
      (!getSettingEnableReferralAdoption() || hasSeenNudge1);

    return {
      shouldShowReengageReferralNudge,
      currentMilestone,
      nudgeToShow,
      hasAcknowledgedReengageReferralNudge,
      setAppNudgeStore,
    };
  };
